import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../../App";

export const GROUP_TYPE_TO_TEXT = {
    system: "System",
    location: "Location",
    other: "Other",
};

function GroupEditForm(props) {
    const { value, onChange, errorMessages } = props;

    const { t } = useTranslation();

    const textField = (id, label, extraProps = {}) => (
        <TextField
            variant="standard"
            id={id}
            label={label}
            value={value[id] ?? ""}
            onChange={(event) => onChange(id, event.target.value)}
            error={errorMessages.hasOwnProperty(id)}
            helperText={errorMessages[id] ? errorMessages[id] : ""}
            {...extraProps}
        />
    );

    return value ? (
        <Stack spacing={2}>
            {textField("name", t("Name"))}
            {textField("type", t("Type"), {
                select: true,
                children: Object.entries(GROUP_TYPE_TO_TEXT).map(([type, text]) => (
                    <MenuItem key={type} value={type}>
                        {t(text)}
                    </MenuItem>
                )),
            })}
        </Stack>
    ) : null;
}

export default function MeteringGroupEditPopUp(props) {
    const { open, onCloseWithoutSaving, onCloseWithUpdate } = props;

    const { t } = useTranslation();

    const [data, setData] = React.useState(props.data);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [errorMessages, setErrorMessages] = React.useState({});

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { groupApi } = React.useContext(ApiContext);

    React.useEffect(() => {
        setData(props.data);
    }, [props.data]);

    const handleFormChange = (id, value) => {
        setData((old) => ({ ...old, [id]: value }));
        setErrorMessages((old) =>
            Object.fromEntries(
                Object.entries(old).filter(([key]) => key !== id),
            ),
        );
    };

    const handleCloseWithoutSaving = () => {
        setData(props.data);
        onCloseWithoutSaving();
        setErrorMessages({});
    };

    const handleSave = () => {
        setErrorMessages({});

        const payload = {
            facility: data.facility,
            name: data.name ?? "",
            type: data.type ?? "",
        };

        const onSuccess = (saved) => {
            onCloseWithUpdate(saved);
        };

        const onError = (response) => {
            setErrorMessages(response);
            const non_field_errors = Object.entries(response)
                .filter(([key]) => key === "non_field_errors")
                .map(([key, value]) => value);
            if (non_field_errors.length > 0) {
                setAlertMessage(non_field_errors.join("; "));
                setAlertOpen(true);
            }
        };

        if (data.id === undefined) {
            groupApi.create(payload, onSuccess, onError);
        } else {
            groupApi.update(
                `${groupApi.baseUrl}${data.id}/`,
                payload,
                onSuccess,
                onError,
            );
        }
    };

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleCloseWithoutSaving}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"sm"}
            >
                <DialogTitle>
                    {data?.id === undefined ? t("Add Group") : t("Edit group")}
                </DialogTitle>
                <DialogContent>
                    <GroupEditForm
                        value={data}
                        onChange={handleFormChange}
                        errorMessages={errorMessages}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWithoutSaving} autoFocus>
                        {t("Cancel")}
                    </Button>
                    <Button onClick={handleSave}>{t("Save")}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem saving data")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
